import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from 'components/base/page-header';
import useDashboardContext from 'contexts/dashboard.context';
import axios, { AxiosRequestConfig } from 'axios';
import { apiService } from 'services/base.service';
import CropImageModal from 'components/modals/crop-image-modal';
import { handleApiError } from 'utils/functions';

const ProfileHeader = ({
  imgSrc,
  setImgSrc
}: {
  imgSrc?: string | null;
  setImgSrc?: (arg1: string) => void;
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isEdit = () => pathname.includes(ROUTE_KEYS.EDIT_PROFILE);
  const firstName = localStorage.getItem(STORAGE_KEYS.FIRST_NAME);
  const lastName = localStorage.getItem(STORAGE_KEYS.LAST_NAME);
  const imgRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [img, setImg] = useState('');
  const [croppedImage, setCroppedImage] = useState('');
  const [imgType, setImgType] = useState('');
  const [imgName, setImgName] = useState('');

  const { activeCompany, refetchActiveCompany } = useDashboardContext();
  const token = localStorage.getItem('token');
  const config: AxiosRequestConfig = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  const handleButtonClick = () => {
    if (isEdit()) {
      imgRef?.current?.click();
    } else {
      navigate(`${ROUTE_KEYS.APP}/${ROUTE_KEYS.EDIT_PROFILE}`);
    }
  };

  const uploadImg = async (imgFile: File) => {
    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', imgFile);
      const res = await axios.post('https://file-api.getfoodcourt.com/upload', data, config);
      if (res?.data) {
        const updateResponse = await apiService.put('company/update', {
          data: {
            logo: res?.data?.url,
            company_id: activeCompany?.id
          }
        });
        if (updateResponse) {
          setHasUploaded(true);
          setImgSrc?.(res?.data?.url);
          refetchActiveCompany();
        }
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setUploading(false);
    }
  };

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setHasUploaded(false);
    const { files } = e.target;
    if (files) {
      setFile(files[0]);
      setImgType(files[0]?.type);
      setImgName(files[0]?.name);
    }
  };

  const closeCropModal = () => {
    if (!hasUploaded) {
      setFile(null);
      setImg('');
      setCroppedImage('');
      setImgType('');
      setImgName('');
    }
    setShowCropModal(false);
  };
  const convertBlobUrlToBlob = async (blobUrl: string) => {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return blob;
  };

  const blobToFile = (blobData: Blob, fileName: string, mimeType: string) => {
    const file = new File([blobData], fileName, { type: mimeType });
    return file;
  };

  const saveImage = async () => {
    setShowCropModal(false);
    const imageBlob = await convertBlobUrlToBlob(croppedImage);
    const imageFile = blobToFile(imageBlob, imgName, imgType);
    uploadImg(imageFile);
  };

  useEffect(() => {
    if (file) {
      setImg(URL.createObjectURL(file));
      setShowCropModal(true);
    } else {
      if (imgRef.current) imgRef.current.value = '';
    }
  }, [file]);

  return (
    <div>
      {showCropModal && img && (
        <CropImageModal
          show={showCropModal}
          onClose={closeCropModal}
          src={img}
          setCroppedImage={setCroppedImage}
          imgType={imgType}
          saveImage={saveImage}
          circularCrop={true}
        />
      )}
      <PageHeader title='Account' subtitle='Manage your account information' />
      <div className='flex gap-3 items-center'>
        {!imgSrc ? (
          <div className='font-semibold flex items-center justify-center w-[50px] h-[50px] md:w-[80px] md:h-[80px] text-base sm:text-lg md:text-[32px] rounded-full text-white  bg-red-5'>
            {firstName?.[0]}
            {lastName?.[0]}
          </div>
        ) : (
          <div
            className='w-[50px] h-[50px] md:w-[80px] md:h-[80px] rounded-full'
            style={{
              backgroundImage: `url(${imgSrc})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
        )}
        <div className='flex flex-col gap-2'>
          <label className='ml-2 font-semibold font-euclid'>{activeCompany.name}</label>
          <button
            disabled={uploading}
            onClick={handleButtonClick}
            className={`${uploading && 'cursor-not-allowed'} rounded-[100px] bg-[#F4F1F1] ${
              isEdit() ? 'w-[129px]' : ' w-[101px]'
            } h-[31px] text-red-5 hover:text-red-3 text-xs transition-all`}>
            {uploading ? 'Uploading ...' : isEdit() ? 'Change Logo' : 'View profile'}
          </button>
        </div>
      </div>
      <input ref={imgRef} type='file' hidden accept={'image/*'} onChange={onFileChange} />
    </div>
  );
};

export default ProfileHeader;
